/**
 * 🧭 NAVBAR — COACHDIA ULTRA PREMIUM (Iter304)
 * ============================================
 *
 * Architecture en 3 zones (desktop) :
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Logo+baseline │  Comprendre · Expérimenter · Analyser  │ Actions │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Mobile : drawer structuré par groupes, CTA en bas.
 * Design : glass-morphism light, bordure or fine, typo serif accents.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, LogOut, LogIn, HelpCircle } from 'lucide-react';
import { useSmoothNavigation } from '../hooks/useSmoothNavigation';
import { useAuth } from '../contexts/AuthContext';
import HelpDesktopModal from './help/HelpDesktopModal';
import HelpMobileSheet from './help/HelpMobileSheet';

/* ─────────────────────────── Navigation groups (desktop dropdowns) ─────────────────────────── */

const NAV_GROUPS = [
  {
    id: 'comprendre',
    label: 'Comprendre',
    items: [
      { id: 'pourquoi', label: 'Vision',     description: 'La méthode CoachDIA',          kind: 'anchor' },
      { id: 'piliers',  label: 'Formation',  description: '20 modules · 5 piliers',       kind: 'anchor' },
    ],
  },
  {
    id: 'experimenter',
    label: 'Expérimenter',
    items: [
      { id: 'coach-ia',         label: 'Coach IA',         description: 'Owen · IA exigeante',  kind: 'anchor', accent: 'violet' },
      { id: 'coaching-humain',  label: 'Coaching Humain',  description: 'Coachs certifiés',      kind: 'anchor', accent: 'gold' },
    ],
  },
  {
    id: 'analyser',
    label: 'Analyser',
    items: [
      { id: '/my-discovery',     label: 'Prism', description: 'Profil comportemental',     kind: 'route' },
      { id: '/team-pulse/teams', label: 'Pulse', description: 'Diagnostic équipe',          kind: 'route' },
    ],
  },
];

/* ─────────────────────────── Atoms ─────────────────────────── */

const Logo = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2.5 group shrink-0"
    data-testid="navbar-logo"
    aria-label="Accueil CoachDIA"
  >
    <div
      className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform"
      style={{
        background: 'linear-gradient(135deg, #1C1147 0%, #3A2680 100%)',
        boxShadow: '0 4px 18px rgba(28,17,71,0.18), inset 0 1px 0 rgba(255,255,255,0.18)',
      }}
    >
      <span className="font-serif text-base font-bold text-[#F3D060]">C</span>
    </div>
    <div className="text-left leading-tight block">
      <div className="font-serif text-[14px] sm:text-[15px] font-bold text-[#1C1147] tracking-tight">CoachDIA</div>
      <div className="text-[8.5px] sm:text-[9px] uppercase tracking-[0.20em] sm:tracking-[0.22em] text-[#B7901F] font-semibold">
        Leadership mesurable
      </div>
    </div>
  </button>
);

const NavGroupButton = ({ group, isOpen, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={isOpen}
    aria-haspopup="true"
    data-testid={`navbar-group-${group.id}`}
    className={
      'inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-medium transition-all ' +
      (isOpen
        ? 'text-[#1C1147] bg-[#FFF8E0]'
        : 'text-[#3A3252] hover:text-[#1C1147] hover:bg-[#FFFCEC]')
    }
  >
    <span>{group.label}</span>
    <ChevronDown
      className={'w-3.5 h-3.5 transition-transform ' + (isOpen ? 'rotate-180' : '')}
      aria-hidden
    />
  </button>
);

const NavGroupDropdown = ({ group, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    transition={{ duration: 0.18 }}
    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white border border-[#E8E4D9] rounded-2xl py-2 z-[120]"
    style={{ boxShadow: '0 18px 44px -14px rgba(28,17,71,0.18)' }}
  >
    <div className="px-4 py-2 text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold">
      {group.label}
    </div>
    {group.items.map((it) => (
      <button
        key={it.id}
        type="button"
        onClick={() => onSelect(it)}
        className="w-full text-left px-4 py-2.5 hover:bg-[#FFF8E0] transition-colors group/item flex items-center gap-3"
        data-testid={`navbar-item-${(it.id || '').replace(/\W+/g, '-')}`}
      >
        <span
          className="w-1 h-8 rounded-full transition-all group-hover/item:h-10"
          style={{
            background:
              it.accent === 'violet'
                ? 'linear-gradient(180deg, #6D28D9, #A78BFA)'
                : it.accent === 'gold'
                ? 'linear-gradient(180deg, #B7901F, #F3D060)'
                : 'linear-gradient(180deg, #D4AF37, #F3D060)',
          }}
          aria-hidden
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#1C1147]">{it.label}</div>
          <div className="text-xs text-[#7A6F58]">{it.description}</div>
        </div>
      </button>
    ))}
  </motion.div>
);

const TextLink = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="text-sm text-[#3A3252] hover:text-[#1C1147] font-medium transition-colors px-3 py-1.5 rounded-full hover:bg-[#FFF8E0]"
  >
    {children}
  </button>
);

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="px-4 py-2 rounded-full font-bold text-sm text-[#1C1147] hover:-translate-y-0.5 transition-transform"
    style={{
      background: 'linear-gradient(135deg, #D4AF37, #F3D060)',
      boxShadow: '0 6px 22px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.45)',
    }}
  >
    {children}
  </button>
);

const SecondaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="px-4 py-2 rounded-full font-semibold text-sm text-[#1C1147] border border-[#E8E4D9] bg-white hover:border-[#D4AF37] hover:bg-[#FFFCEC] transition-all"
  >
    {children}
  </button>
);

/* Iter305 — Bouton Auth premium (Connexion / Déconnexion) avec relief */
const AuthPremiumButton = ({ children, onClick, testId, icon: Icon, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={
      'inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5 active:scale-[0.98] ' +
      (danger
        ? 'text-[#1C1147] hover:bg-[#FFF1ED] hover:border-[#B94A48] hover:text-[#B94A48]'
        : 'text-[#1C1147] hover:bg-[#FFFCEC]')
    }
    style={{
      background: '#FFFFFF',
      border: '1.5px solid rgba(212,175,55,0.45)',
      boxShadow:
        '0 6px 18px -8px rgba(28,17,71,0.18), inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(212,175,55,0.12)',
    }}
  >
    {Icon ? <Icon className="w-4 h-4" /> : null}
    {children}
  </button>
);

/* ─────────────────────────── Navbar ─────────────────────────── */

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // 🧭 Help navigation modals (desktop + mobile)
  const [helpOpen, setHelpOpen] = useState(false);
  const [openGroupId, setOpenGroupId] = useState(null);
  const dropdownRef = useRef(null);
  const { scrollToSection } = useSmoothNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Lock body scroll when mobile drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close desktop dropdown on outside click
  useEffect(() => {
    if (!openGroupId) return undefined;
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenGroupId(null);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [openGroupId]);

  const goToAnchor = useCallback((sectionId) => {
    setMobileOpen(false);
    setOpenGroupId(null);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToSection(sectionId), 600);
    } else {
      setTimeout(() => scrollToSection(sectionId), 200);
    }
  }, [location.pathname, navigate, scrollToSection]);

  const goToRoute = useCallback((path) => {
    setMobileOpen(false);
    setOpenGroupId(null);
    navigate(path);
  }, [navigate]);

  const handleItem = useCallback((it) => {
    if (it.kind === 'route') goToRoute(it.id);
    else goToAnchor(it.id);
  }, [goToAnchor, goToRoute]);

  const handleLogout = useCallback(() => {
    setMobileOpen(false);
    logout();
    navigate('/');
  }, [logout, navigate]);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-[102] transition-all duration-500 border-b"
        style={{
          background: isScrolled ? 'rgba(250,247,239,0.85)' : 'rgba(250,247,239,0.65)',
          borderColor: isScrolled ? 'rgba(212,175,55,0.22)' : 'rgba(212,175,55,0.12)',
          backdropFilter: 'blur(14px) saturate(180%)',
          WebkitBackdropFilter: 'blur(14px) saturate(180%)',
        }}
        data-testid="navbar"
      >
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Zone gauche : Logo */}
            <Logo onClick={() => goToAnchor('accueil')} />

            {/* Zone centrale : navigation groupée (desktop only) */}
            <div ref={dropdownRef} className="hidden lg:flex items-center gap-1 relative">
              {NAV_GROUPS.map((g) => (
                <div key={g.id} className="relative">
                  <NavGroupButton
                    group={g}
                    isOpen={openGroupId === g.id}
                    onToggle={() => setOpenGroupId((v) => (v === g.id ? null : g.id))}
                  />
                  <AnimatePresence>
                    {openGroupId === g.id && <NavGroupDropdown group={g} onSelect={handleItem} />}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* Zone droite : actions (desktop only) — Iter306 : Mes sessions + Mon profil supprimés (doublons fonctionnels) */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              {/* 🧭 Aide & Navigation — toujours visible desktop */}
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                data-testid="navbar-help-desktop-btn"
                aria-label="Ouvrir l'aide à la navigation"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.03]"
                style={{
                  background: 'rgba(255,252,236,0.8)',
                  color: '#1C1147',
                  border: '1px solid rgba(212,175,55,0.4)',
                }}
              >
                <HelpCircle className="w-3.5 h-3.5" style={{ color: '#B7901F' }} />
                Aide & Navigation
              </button>
              {user ? (
                <>
                  <PrimaryCTA onClick={() => goToRoute('/dashboard')} testId="navbar-dashboard">
                    Mon Dashboard
                  </PrimaryCTA>
                  <SecondaryCTA onClick={() => goToRoute('/marketplace')} testId="navbar-coach-space">
                    Espace Coach
                  </SecondaryCTA>
                  <span className="w-px h-5 bg-[#E8E4D9]" aria-hidden />
                  <AuthPremiumButton onClick={handleLogout} testId="navbar-logout" icon={LogOut} danger>
                    Déconnexion
                  </AuthPremiumButton>
                </>
              ) : (
                <>
                  <TextLink onClick={() => goToRoute('/affiliation')} testId="navbar-affiliation-public">
                    Affiliation
                  </TextLink>
                  <AuthPremiumButton onClick={() => goToRoute('/login')} testId="navbar-login" icon={LogIn}>
                    Connexion
                  </AuthPremiumButton>
                  <PrimaryCTA onClick={() => goToAnchor('plans')} testId="navbar-cta-pricing">
                    Commencer
                  </PrimaryCTA>
                </>
              )}
            </div>

            {/* Mobile burger */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 rounded-full hover:bg-[#FFF8E0] text-[#1C1147]"
              aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={mobileOpen}
              data-testid="navbar-mobile-burger"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[110] bg-[#1C1147]/30 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 260 }}
              className="fixed top-0 right-0 bottom-0 z-[111] w-[88%] max-w-[400px] bg-[#FAF7EF] shadow-2xl flex flex-col lg:hidden"
              data-testid="navbar-mobile-drawer"
            >
              {/* Header drawer */}
              <div className="flex items-center justify-between px-5 h-16 border-b border-[#E8E4D9]">
                <Logo onClick={() => goToAnchor('accueil')} />
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-full hover:bg-[#FFF8E0] text-[#1C1147]"
                  aria-label="Fermer le menu"
                  data-testid="navbar-mobile-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Groups */}
              <div className="flex-1 overflow-y-auto px-5 py-6 space-y-7">
                {/* 🧭 Aide & Navigation — premier CTA mobile */}
                <button
                  type="button"
                  onClick={() => { setMobileOpen(false); setHelpOpen(true); }}
                  data-testid="navbar-help-mobile-btn"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold transition-transform active:scale-[0.98]"
                  style={{
                    background: 'linear-gradient(135deg, #B7901F, #D4AF37, #F3D060)',
                    color: '#1C1147',
                    boxShadow: '0 8px 22px -10px rgba(212,175,55,0.6)',
                  }}
                >
                  <HelpCircle className="w-4 h-4" />
                  Aide & Navigation
                </button>
                {NAV_GROUPS.map((g) => (
                  <div key={g.id}>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-3">
                      {g.label}
                    </p>
                    <div className="space-y-1">
                      {g.items.map((it) => (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => handleItem(it)}
                          className="w-full text-left px-4 py-3 rounded-xl hover:bg-[#FFF8E0] active:bg-[#FFE9A0] transition-all flex items-center gap-3"
                          data-testid={`navbar-mobile-item-${(it.id || '').replace(/\W+/g, '-')}`}
                        >
                          <span
                            className="w-1 h-9 rounded-full"
                            style={{
                              background:
                                it.accent === 'violet'
                                  ? 'linear-gradient(180deg, #6D28D9, #A78BFA)'
                                  : it.accent === 'gold'
                                  ? 'linear-gradient(180deg, #B7901F, #F3D060)'
                                  : 'linear-gradient(180deg, #D4AF37, #F3D060)',
                            }}
                            aria-hidden
                          />
                          <div className="flex-1">
                            <div className="text-base font-semibold text-[#1C1147]">{it.label}</div>
                            <div className="text-xs text-[#7A6F58]">{it.description}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Section utilisateur — Iter305 : tout regroupé ici */}
                {user && (
                  <div className="pt-2 border-t border-[#E8E4D9]">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-3 mt-5">
                      Mon espace
                    </p>
                    <div className="space-y-1">
                      <button
                        type="button"
                        onClick={() => goToRoute('/dashboard')}
                        className="w-full text-left px-4 py-3 rounded-xl hover:bg-[#FFF8E0] active:bg-[#FFE9A0] transition-all flex items-center gap-3"
                        data-testid="navbar-mobile-dashboard"
                      >
                        <span
                          className="w-1 h-9 rounded-full"
                          style={{ background: 'linear-gradient(180deg, #B7901F, #F3D060)' }}
                          aria-hidden
                        />
                        <div className="flex-1">
                          <div className="text-base font-bold text-[#1C1147]">Mon Dashboard</div>
                          <div className="text-xs text-[#7A6F58]">Formation et progression</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => goToRoute('/marketplace')}
                        className="w-full text-left px-4 py-3 rounded-xl hover:bg-[#FFF8E0] active:bg-[#FFE9A0] transition-all flex items-center gap-3"
                        data-testid="navbar-mobile-coach-space"
                      >
                        <span
                          className="w-1 h-9 rounded-full"
                          style={{ background: 'linear-gradient(180deg, #6D28D9, #A78BFA)' }}
                          aria-hidden
                        />
                        <div className="flex-1">
                          <div className="text-base font-semibold text-[#1C1147]">Espace Coach</div>
                          <div className="text-xs text-[#7A6F58]">Trouver mon coach</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => goToRoute('/affiliation')}
                        className="w-full text-left px-4 py-3 rounded-xl hover:bg-[#FFF8E0] active:bg-[#FFE9A0] transition-all flex items-center gap-3"
                        data-testid="navbar-mobile-affiliation"
                      >
                        <span
                          className="w-1 h-9 rounded-full bg-[#E8E4D9]"
                          aria-hidden
                        />
                        <div className="flex-1">
                          <div className="text-base font-semibold text-[#1C1147]">Affiliation</div>
                          <div className="text-xs text-[#7A6F58]">Programme partenaire</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA footer — Iter305 : Déconnexion premium uniquement (auth) */}
              <div className="px-5 pt-4 pb-5 border-t border-[#E8E4D9] bg-white space-y-2.5">
                {user ? (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full px-4 py-3 rounded-full font-bold text-sm text-[#1C1147] flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 active:scale-[0.98] hover:bg-[#FFF1ED] hover:border-[#B94A48] hover:text-[#B94A48]"
                    style={{
                      background: '#FFFFFF',
                      border: '1.5px solid rgba(212,175,55,0.45)',
                      boxShadow: '0 6px 18px -8px rgba(28,17,71,0.18), inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(212,175,55,0.12)',
                    }}
                    data-testid="navbar-mobile-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    Déconnexion
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => goToAnchor('plans')}
                      className="w-full px-4 py-3 rounded-full font-bold text-[#1C1147] text-sm"
                      style={{
                        background: 'linear-gradient(135deg, #D4AF37, #F3D060)',
                        boxShadow: '0 6px 22px -8px rgba(212,175,55,0.55)',
                      }}
                      data-testid="navbar-mobile-cta-pricing"
                    >
                      Commencer
                    </button>
                    <button
                      type="button"
                      onClick={() => goToRoute('/login')}
                      className="w-full px-4 py-3 rounded-full font-bold text-sm text-[#1C1147] flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 active:scale-[0.98] hover:bg-[#FFFCEC]"
                      style={{
                        background: '#FFFFFF',
                        border: '1.5px solid rgba(212,175,55,0.45)',
                        boxShadow: '0 6px 18px -8px rgba(28,17,71,0.18), inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(212,175,55,0.12)',
                      }}
                      data-testid="navbar-mobile-login"
                    >
                      <LogIn className="w-4 h-4" />
                      Connexion
                    </button>
                  </>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* 🧭 Modales d'aide — desktop (lg+) vs mobile (en-dessous de lg) */}
      <div className="hidden lg:block">
        <HelpDesktopModal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          userContext={{
            role: user?.role,
            isAffiliate: user?.role === 'affiliate',
            isB2BAdmin: false,
          }}
        />
      </div>
      <div className="lg:hidden">
        <HelpMobileSheet
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          userContext={{
            role: user?.role,
            isAffiliate: user?.role === 'affiliate',
            isB2BAdmin: false,
          }}
        />
      </div>
    </>
  );
};

export default Navbar;
